import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Type,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CartFacade } from '../../store/cart';
import { YandexMetrikaService } from '@services/utils/yandex-metrika.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { BuildType } from '@shared/enums/build.type';
import { Platform } from '@shared/enums/platform.enum';
import { ScrollService } from './components/page-header/services/scroll.service';
import { HeaderHeightService } from './components/page-header/services/header-height.service';
import { MiniCartPopoverService } from './components/mini-cart/mini-cart-popover.service';
import { AppStoreFacade } from '../../store/app-store/app-store.facade';
import { EnvironmentService } from '@services/utils/environment.service';
import { PlatformService } from '@services/utils/platform.service';
import { ConsentDialogService } from '@services/dialogs/consent-dialog.service';
import { PageHeaderModeService } from './components/page-header/services/page-header-mode.service';
import { StartingPriceBannerComponent } from './components/starting-price-banner/starting-price-banner.component';
import { getBottomOverlayHeight } from './utils/get-bottom-overlay-height';
import { OnlinePaymentAnnouncementDialogService } from './components/online-payment-announcement/services/online-payment-announcement-dialog.service';

@Component({
  selector: 'b2b-portal-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CartFacade, ScrollService, HeaderHeightService, MiniCartPopoverService, OnlinePaymentAnnouncementDialogService],
  standalone: false,
  host: {
    '[style.--mobile-menu-height]': 'mobileMenuHeight()',
    '[style.--page-header-height]': 'pageHeaderHeight()',
    '[style.--page-bottom-overlay-height]': 'bottomOverlayHeight()',
  },
})
export class LayoutComponent implements OnInit, OnDestroy {
  isNarrowLayout: Signal<boolean> = this.appStoreFacade.$isNarrowLayout;

  protected readonly BuildTypesEnum = BuildType;
  protected readonly Platform = Platform;

  #environmentService: EnvironmentService = inject(EnvironmentService);
  #consentDialogService: ConsentDialogService = inject(ConsentDialogService);
  #googleAnalyticsService = inject(GoogleAnalyticsService);
  #pageHeaderModeService: PageHeaderModeService = inject(PageHeaderModeService);
  #platformService: PlatformService = inject(PlatformService);
  #onlinePaymentAnnouncementDialogService = inject(OnlinePaymentAnnouncementDialogService);
  #destroyRef = inject(DestroyRef);
  #injector = inject(Injector);
  private readonly appHeader = viewChild.required<ElementRef<HTMLElement>>('appHeader');
  private readonly startingPriceBanner = viewChild.required(StartingPriceBannerComponent, { read: ElementRef });
  private layoutOffsetsObserver?: ResizeObserver;
  private mobileMenuElement: Element | null = null;

  // Real rendered sizes for pages that fit into the viewport: header above the page body (any build)
  // and fixed overlays pinned to the viewport bottom (navigation, starting price banner).
  protected readonly pageHeaderHeight = signal('0px');
  protected readonly bottomOverlayHeight = signal('0px');

  readonly isTurkishBuild = computed(() => this.#environmentService.isBuildType(BuildType.tr));
  readonly isCompactHeaderVisible = this.#pageHeaderModeService.isCompactHeaderVisible;

  mobileMenuHeight = signal<string>('0px');
  protected readonly chatBotComponent = signal<Type<unknown> | null>(null);
  protected readonly turkishHeaderComponent = signal<Type<unknown> | null>(null);
  private resizeObserver?: ResizeObserver;

  constructor(
    private yandexMetrikaService: YandexMetrikaService,
    private appStoreFacade: AppStoreFacade,
  ) {
    // The bottom menu is rendered only on mobile/tablet, so the height observer is re-attached
    // whenever the platform changes (e.g. the window is resized across a breakpoint).
    effect(() => {
      this.#platformService.platform();
      setTimeout(() => this.observeMobileMenu(), 0);
    });

    afterNextRender(() => {
      this.layoutOffsetsObserver = new ResizeObserver(() => this.updateLayoutOffsets());
      this.layoutOffsetsObserver.observe(this.appHeader().nativeElement);
      this.layoutOffsetsObserver.observe(this.startingPriceBanner().nativeElement);
    });
  }

  ngOnInit() {
    this.yandexMetrikaService.addMetricScript();
    this.#googleAnalyticsService.addScript();
    this.#consentDialogService.subscribeToLogout();
    // The online payment announcement goes after the consent dialogs, never on top of them.
    this.#consentDialogService
      .openConsentDialog(this.isTurkishBuild())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#onlinePaymentAnnouncementDialogService.open());

    if (this.#environmentService.isBuildType(BuildType.ru)) {
      void this.loadChatBot();
    }

    if (this.isTurkishBuild()) {
      void this.loadTurkishHeader();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.layoutOffsetsObserver?.disconnect();
  }

  private observeMobileMenu(): void {
    this.resizeObserver?.disconnect();
    const mobileMenu = document.querySelector('b2b-navigation-mobile-menu');
    this.mobileMenuElement = mobileMenu;

    if (mobileMenu) {
      this.resizeObserver = new ResizeObserver(() => {
        const height = mobileMenu.getBoundingClientRect().height;
        this.mobileMenuHeight.set(`${height}px`);
        this.scheduleLayoutOffsetsUpdate();
      });

      this.resizeObserver.observe(mobileMenu);

      const height = mobileMenu.getBoundingClientRect().height;
      this.mobileMenuHeight.set(`${height}px`);
    } else {
      this.mobileMenuHeight.set('0px');
    }

    this.scheduleLayoutOffsetsUpdate();
  }

  // The banner is positioned from --mobile-menu-height, so its offset is read only after the render
  // that applied the new menu height.
  private scheduleLayoutOffsetsUpdate(): void {
    afterNextRender(() => this.updateLayoutOffsets(), { injector: this.#injector });
  }

  private updateLayoutOffsets(): void {
    this.pageHeaderHeight.set(`${this.appHeader().nativeElement.getBoundingClientRect().height}px`);

    const overlays = [this.mobileMenuElement, this.startingPriceBanner().nativeElement]
      .filter((element): element is Element => element !== null)
      .map(element => {
        const { top, height } = element.getBoundingClientRect();
        return { top, height, isFixed: window.getComputedStyle(element).position === 'fixed' };
      });

    this.bottomOverlayHeight.set(`${getBottomOverlayHeight(window.innerHeight, overlays)}px`);
  }

  private async loadTurkishHeader(): Promise<void> {
    const { PageHeaderTRComponent } = await import('./components/page-header/page-header-tr/page-header-tr.component');

    this.turkishHeaderComponent.set(PageHeaderTRComponent);
  }

  private async loadChatBot(): Promise<void> {
    const { ChatBotComponent } = await import('../../scripts/chat-bot/chat-bot.component');

    this.chatBotComponent.set(ChatBotComponent);
  }
}
